import { useLoading } from "../../loading/LoadingContext"
import { useEffect, useState } from "react"
import { PrimeSpeedRow } from "../../common/common.types"
import { apiFetch, apiFetchBody } from '../../utility/fetch'

const useAmazonPageSpeedViewsData = (startDate: string | null, endDate: string | null, userData) => {
  const { toggleLoading } = useLoading()
  const [file, setFile] = useState<File | null>(null)
  const [data, setData] = useState<PrimeSpeedRow[] | undefined>()
  const [error, setError] = useState<any>()
  const [refetch, setRefetch] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      toggleLoading(true)
      try {
        const response = await apiFetchBody<PrimeSpeedRow[]>({
          endpoint: 'amazonPageSpeedViews',
          method: 'GET',
          accessToken: userData?.accessToken,
          params: [
            { key: 'startDate', value: startDate },
            { key: 'endDate', value: endDate }
          ]
        });
        setData(response)
      } catch (err) {
        setError(err)
      } finally {
        toggleLoading(false)
      }
    };

    if (startDate && endDate && userData?.accessToken) {
      fetchData();
    }
  }, [startDate, endDate, userData, refetch]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setFile(e.target.files[0])
    }
  };

  const uploadFile = async () => {
    if (!file) return alert('Please select a file.')

    const reader = new FileReader()
    reader.onload = async (event) => {
      toggleLoading(true)
      try {
        const text = event.target?.result as string;
        const [headers, ...rows] = text.split('\n').map(row => row.split('\t'))
        const expectedHeaders = ['ASIN', 'Date', 'Size Tier', 'Same-day detail page views', '<= 1 day detail page views', '<= 2 days detail page views', '> 2 days detail page views']

        const headersMatch = expectedHeaders.every((h, i) => h === headers[i])
        if (!headersMatch) return alert('The file headers do not match the expected format.')

        const formData = new FormData()
        formData.append('tsvContent', file)

        const response = await apiFetch({
          endpoint: 'amazonPageSpeedViews',
          method: 'POST',
          accessToken: userData.accessToken,
          headers: { 'Content-Type': 'multipart/form-data' },
          body: formData,
        })

        if (!response.ok) {
          setError(response.body)
          console.error('Error uploading file:', response.body)
        } else {
          setRefetch(prev => !prev); // trigger data reload
        }
      } catch (err) {
        setError(err)
        console.error('Error uploading file:', err)
      } finally {
        toggleLoading(false)
      }
    };

    reader.onerror = (err) => {
      setError(err)
      alert('Error reading file.')
      toggleLoading(false)
    };

    reader.readAsText(file)
  }

  return {
    data,
    error,
    handleFileChange,
    uploadFile
  }
}

export default useAmazonPageSpeedViewsData